import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dialog, Section, Box, Flex, Text, Button, Table, TextField } from '@radix-ui/themes';

import PageTitle from 'components/layout/PageTitle';
import { ConfirmDialog } from 'components/dialog';
import { instance } from 'api/axios.instance';
import { isFormUsed, toDateInput } from 'util/formVote';

// HWPX 양식 관리 > 목록
export default function FormTemplateList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [isOpenIsUsedDialog, setIsOpenIsUsedDialog] = useState(false);
  const [usageTarget, setUsageTarget] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await instance('/admin/form-template/list', {
        method: 'GET',
        params: { keyword: searchParams.get('keyword') || '' }
      });
      setTemplates(response.data.data || []);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('keyword', keyword);
      return next;
    });
  };

  const handleDelete = async (formId, formName) => {
    if (!window.confirm(`"${formName}" 양식을 삭제하시겠습니까?`)) return;
    await instance(`/admin/form-template/${formId}`, { method: 'DELETE' });
    fetchList();
  };

  const openUsageDialog = template => {
    setUsageTarget({ formId: template.formId, nextIsUsed: !isFormUsed(template) });
    setIsOpenIsUsedDialog(true);
  };

  const handleUpdateIsUsed = async () => {
    if (!usageTarget) return;
    const detail = await instance(`/admin/form-template/${usageTarget.formId}`, { method: 'GET' });
    const form = detail.data.data;
    const response = await instance(`/admin/form-template/edit/${usageTarget.formId}`, {
      method: 'POST',
      data: {
        formCode: form.formCode,
        formName: form.formName,
        category: form.category || '',
        description: form.description || '',
        fieldDef: form.fieldDef || '[]',
        isUsed: usageTarget.nextIsUsed,
        useYn: usageTarget.nextIsUsed ? 'Y' : 'N',
        startDate: toDateInput(form.startDate),
        endDate: toDateInput(form.endDate)
      }
    });
    if (response.data.status === 200 || response.data.status === 201) {
      await fetchList();
    }
  };

  return (
    <Section>
      <PageTitle title="양식 관리" />

      <Box className="box box-white">
        <Flex justify="between" align="center" mb="4">
          <Flex gap="2">
            <TextField.Root
              placeholder="양식명 검색"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
            <Button variant="outline" onClick={handleSearch}>
              검색
            </Button>
          </Flex>
          <Button onClick={() => navigate('/formTemplate/write')}>양식 등록</Button>
        </Flex>

        {!loading && templates.length === 0 ? (
          <Flex direction="column" align="center" justify="center" gap="4" py="8">
            <Text color="gray">등록된 양식이 없습니다.</Text>
          </Flex>
        ) : (
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell justify="center" width="160px">
                  코드
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="center">양식명</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="center" width="90px">
                  카테고리
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="center" width="80px">
                  버전
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="center" width="108px">
                  사용 여부
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="center" width="220px">
                  사용 기간
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="center" width="155px">
                  등록일
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell justify="center" width="170px">
                  관리
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {templates.map(tpl => (
                <Table.Row key={tpl.formId}>
                  <Table.Cell>{tpl.formCode}</Table.Cell>
                  <Table.Cell>
                    <Text
                      style={{ cursor: 'pointer', color: 'var(--accent-9)' }}
                      onClick={() => navigate(`/formTemplate/detail/${tpl.formId}`)}
                    >
                      {tpl.formName}
                    </Text>
                  </Table.Cell>
                  <Table.Cell justify="center">{tpl.category || '-'}</Table.Cell>
                  <Table.Cell justify="center">v{tpl.currentVer}</Table.Cell>
                  <Table.Cell justify="center">
                    <Button size="1" variant="outline" color={isFormUsed(tpl) ? undefined : 'gray'} onClick={() => openUsageDialog(tpl)}>
                      {isFormUsed(tpl) ? '사용' : '사용안함'}
                    </Button>
                  </Table.Cell>
                  <Table.Cell justify="center">
                    {tpl.startDate && tpl.endDate ? `${toDateInput(tpl.startDate)} ~ ${toDateInput(tpl.endDate)}` : '-'}
                  </Table.Cell>
                  <Table.Cell justify="center">{tpl.regDt}</Table.Cell>
                  <Table.Cell justify="center">
                    <Flex gap="2">
                      <Button size="1" variant="outline" onClick={() => navigate(`/formTemplate/write/${tpl.formId}`)}>
                        수정
                      </Button>
                      <Button size="1" variant="outline" color="red" onClick={() => handleDelete(tpl.formId, tpl.formName)}>
                        삭제
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>

      {isOpenIsUsedDialog && (
        <Dialog.Root open={isOpenIsUsedDialog} onOpenChange={setIsOpenIsUsedDialog}>
          <ConfirmDialog
            title="사용 여부 변경"
            description={
              <>
                사용여부를 <span className="font-info">‘{usageTarget?.nextIsUsed ? '사용' : '사용안함'}’</span>으로 변경 하시겠습니까?
              </>
            }
            onConfirm={handleUpdateIsUsed}
          />
        </Dialog.Root>
      )}
    </Section>
  );
}
